import React from 'react';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

export interface Progress {
  study_status: string;
  run_status: string | null;
  stages: string[];
  completed_stages: string[];
  current_stage: string | null;
  elapsed_seconds: number | null;
  error: string | null;
}

const STAGE_LABEL: Record<string, string> = {
  quality: 'Signal quality',
  beat_detection: 'Beat detection',
  fusion: 'Cross-channel fusion',
  clustering: 'Morphology clustering',
  classification: 'Beat classification',
  segmentation: 'Rhythm segmentation',
  aggregation: 'Statistics',
  strips: 'Strip selection',
};

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

/**
 * Shown in place of the review workspace while a study has no completed run yet.
 *
 * The engine has no per-stage progress percentage — only which stages have finished — so this
 * shows a checklist rather than a bar with a number, which would be a guess dressed as a fact.
 */
export const AnalysisProgress: React.FC<{ progress: Progress }> = ({ progress }) => {
  // Checked before every other branch, including the FAILED-run one below: a parked study usually
  // *also* has a failed run, and "it will retry" is precisely the wrong thing to say about it.
  // `ANALYSIS_FAILED` means the job is parked and no worker will ever claim it again (§13.6 Q5).
  if (progress.study_status === 'ANALYSIS_FAILED') {
    return (
      <div className="glass-panel space-y-2 rounded-2xl border-rose-900 p-6 text-sm text-rose-200">
        <p className="flex items-center gap-2 font-semibold">
          <XCircle className="h-4 w-4" /> Analysis stopped
        </p>
        <p className="text-xs text-rose-300">{progress.error ?? 'No error was recorded.'}</p>
        <p className="text-xs text-slate-400">
          This study will not retry on its own. The recording itself is intact — ask an
          administrator to re-run the analysis.
        </p>
      </div>
    );
  }

  if (progress.study_status === 'AWAITING_ANALYSIS' && !progress.run_status) {
    return (
      <div className="glass-panel space-y-2 rounded-2xl p-6 text-sm text-slate-300">
        <p className="flex items-center gap-2 font-semibold text-white">
          <Loader2 className="h-4 w-4 animate-spin text-cyan-400" /> Queued for analysis
        </p>
        <p className="text-xs text-slate-400">
          The upload finished; a worker will pick this study up shortly. This page updates on its own.
        </p>
      </div>
    );
  }

  // A failed run while the study is *not* parked: attempts remain, so a retry really is coming.
  if (progress.run_status === 'FAILED') {
    return (
      <div className="glass-panel space-y-2 rounded-2xl border-rose-900 p-6 text-sm text-rose-200">
        <p className="flex items-center gap-2 font-semibold">
          <XCircle className="h-4 w-4" /> Analysis failed
        </p>
        <p className="text-xs text-rose-300">{progress.error ?? 'No error was recorded.'}</p>
        <p className="text-xs text-slate-400">It will retry automatically, up to a limit, before it is parked.</p>
      </div>
    );
  }

  return (
    <div className="glass-panel space-y-3 rounded-2xl p-6">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-sm font-semibold text-white">
          <Loader2 className="h-4 w-4 animate-spin text-cyan-400" /> Analysing
        </p>
        {progress.elapsed_seconds != null && (
          <span className="mono text-xs text-slate-400">{formatElapsed(progress.elapsed_seconds)} elapsed</span>
        )}
      </div>
      <div className="space-y-1.5">
        {progress.stages.map((stage) => {
          const done = progress.completed_stages.includes(stage);
          const current = stage === progress.current_stage;
          return (
            <div key={stage} className={`flex items-center gap-2 text-xs ${done ? 'text-emerald-300' : current ? 'text-cyan-300' : 'text-slate-500'}`}>
              {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : current ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <span className="h-3.5 w-3.5 rounded-full border border-slate-700" />}
              {STAGE_LABEL[stage] ?? stage}
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-500">Full-disclosure signal is already viewable below while analysis runs.</p>
    </div>
  );
};
